import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Lock, FileClock, Accessibility, ShieldCheck } from 'lucide-react';
import { Reveal } from './Reveal';
import { LogoMarquee } from './LogoMarquee';
import { useCountUp } from './useCountUp';
import { fadeUpSm, staggerContainer, useRevealProps } from './motion';

/**
 * TRUST BAND — calm. Reuses the LogoMarquee, then a row of BIG count-up
 * figures that are TRUE for this product, plus an honest "安心" signal row
 * (encryption / retention / accessibility / SOC2 in progress).
 */
export function TrustBand() {
  const { t } = useTranslation();

  return (
    <section data-landing className="border-t border-border bg-card/40">
      <LogoMarquee />

      <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <Reveal as="div" className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
            <span className="sparkle-dot" aria-hidden />
            {t('landing.trust.eyebrow')}
          </span>
          <p className="mt-4 text-lg text-muted-foreground">{t('landing.trust.line')}</p>
        </Reveal>

        <motion.dl
          variants={staggerContainer}
          {...useRevealProps()}
          className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10"
        >
          <Figure target={14} suffix="" labelKey="landing.trust.figLocales" />
          <Figure target={130} suffix="+" labelKey="landing.trust.figCurrencies" bordered />
          <Figure target={5} suffix="" labelKey="landing.trust.figRails" sub="全銀 / SWIFT / ACH / SEPA / Wise" bordered />
          <Figure target={9} suffix="" labelKey="landing.trust.figAgents" bordered />
        </motion.dl>

        <Reveal as="ul" delay={0.1} className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Assurance Icon={Lock} k="enc" />
          <Assurance Icon={FileClock} k="retention" />
          <Assurance Icon={Accessibility} k="a11y" />
          <Assurance Icon={ShieldCheck} k="soc2" />
        </Reveal>
      </div>
    </section>
  );
}

function Figure({
  target,
  suffix,
  labelKey,
  sub,
  bordered,
}: {
  target: number;
  suffix: string;
  labelKey: string;
  sub?: string;
  bordered?: boolean;
}) {
  const { t } = useTranslation();
  const count = useCountUp(target, 1300);
  return (
    <motion.div
      variants={fadeUpSm}
      className={'flex flex-col ' + (bordered ? 'lg:border-l border-border/70 lg:pl-6' : '')}
    >
      <dd className="num text-4xl lg:text-5xl font-semibold text-foreground">
        <span ref={count.ref as React.RefObject<HTMLSpanElement>}>{Math.round(count.value)}</span>
        {suffix}
      </dd>
      <dt className="mt-2 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        {t(labelKey)}
      </dt>
      {sub && <span className="num mt-1 text-xs text-muted-foreground/80">{sub}</span>}
    </motion.div>
  );
}

function Assurance({ Icon, k }: { Icon: typeof Lock; k: string }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4">
      <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-accent-50 dark:bg-accent-900/40 text-accent-700 dark:text-accent-200 flex items-center justify-center">
        <Icon size={16} aria-hidden />
      </span>
      <span>
        <span className="block text-sm font-medium text-foreground">
          {t(`landing.trust.assure.${k}.label`)}
        </span>
        <span className="block text-xs text-muted-foreground mt-0.5 leading-relaxed">
          {t(`landing.trust.assure.${k}.note`)}
        </span>
      </span>
    </li>
  );
}
